/**
 * kpiStyles.js
 * Injects global CSS variables and shared styles into the document once.
 * Import and call injectKpiStyles() in your top-level component.
 */

export const injectKpiStyles = () => {
  if (document.getElementById("kpi-system-styles")) return;

  // Load fonts
  const link = document.createElement("link");
  link.rel = "stylesheet";
  
  document.head.appendChild(link);

  

  const s = document.createElement("style");
  s.id = "kpi-system-styles";
  s.textContent = `
    *, *::before, *::after { box-sizing: border-box; }

    :root {
      --ink:       #0d0d0d;
      --ink-2:     #3d3d3d;
      --ink-3:     #6b7280;
      --ink-4:     #9ca3af;
      --rule:      #e5e7eb;
      --rule-2:    #f3f4f6;
      --surface:   #ffffff;
      --surface-2: #f9fafb;
      --surface-3: #f3f4f6;
      --accent:    #2563eb;
      --accent-2:  #dbeafe;
      --accent-3:  #eff6ff;
      --green:     #16a34a;
      --green-2:   #dcfce7;
      --amber:     #d97706;
      --amber-2:   #fef3c7;
      --red:       #dc2626;
      --red-2:     #fee2e2;
      --purple:    #7c3aed;
      --purple-2:  #ede9fe;
      --font-sans: 'Syne', sans-serif;
      --font-mono: 'Fira Code', monospace;
    }

    /* ── Base font ── */
    // .kpi-root * { font-family: var(--font-sans); }

    /* ── Inputs ── */
    .kpi-input {
      width: 100%;
      padding: 8px 12px;
      border: 1.5px solid var(--rule);
      border-radius: 8px;
      font-size: 13px;
      font-family: var(--font-sans);
      color: var(--ink);
      background: var(--surface);
      outline: none;
      transition: border-color 0.15s, box-shadow 0.15s;
      appearance: auto;
    }
    .kpi-input:focus {
      border-color: var(--accent);
      box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
    }
    .kpi-input::placeholder { color: var(--ink-4); font-size: 12.5px; }

    .kpi-label {
      display: block;
      font-size: 10.5px;
      font-weight: 700;
      color: var(--ink-3);
      text-transform: uppercase;
      letter-spacing: 0.07em;
      font-family: var(--font-sans);
      margin-bottom: 6px;
    }

    /* ── Buttons ── */
    .kpi-btn-primary {
      padding: 9px 22px;
      background: var(--ink);
      color: #fff;
      border: none;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 700;
      font-family: var(--font-sans);
      cursor: pointer;
      letter-spacing: 0.02em;
      transition: all 0.15s;
    }
    .kpi-btn-primary:hover { background: #1a1a1a; transform: translateY(-1px); }
    .kpi-btn-primary:disabled { background: var(--rule); color: var(--ink-4); cursor: not-allowed; transform: none; }

    .kpi-btn-secondary {
      padding: 9px 16px;
      background: transparent;
      color: var(--ink-3);
      border: 1.5px solid var(--rule);
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      font-family: var(--font-sans);
      cursor: pointer;
      transition: all 0.15s;
    }
    .kpi-btn-secondary:hover { background: var(--surface-2); border-color: #d1d5db; }

    /* ── Row action buttons ── */
    .kpi-action-btn {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 5px 11px;
      border-radius: 6px;
      font-size: 11.5px;
      font-weight: 700;
      font-family: var(--font-sans);
      cursor: pointer;
      border: 1.5px solid transparent;
      transition: all 0.15s ease;
      letter-spacing: 0.02em;
      white-space: nowrap;
      line-height: 1;
    }
    .kpi-action-btn:hover { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
    .kpi-action-btn:active { transform: translateY(0); }

    .kpi-btn-add     { background:#eff6ff; color:#2563eb; border-color:#bfdbfe; }
    .kpi-btn-add:hover { background:#dbeafe; }
    .kpi-btn-chat    { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
    .kpi-btn-chat:hover { background:#dcfce7; }
    .kpi-btn-audit   { background:#fff7ed; color:#ea580c; border-color:#fed7aa; }
    .kpi-btn-audit:hover { background:#ffedd5; }
    .kpi-btn-assign  { background:#faf5ff; color:#7c3aed; border-color:#ddd6fe; }
    .kpi-btn-assign:hover { background:#ede9fe; }
    .kpi-btn-history { background:#f8fafc; color:#374151; border-color:#e2e8f0; }
    .kpi-btn-history:hover { background:#f1f5f9; }

    /* ── Checkbox ── */
    .kpi-checkbox {
      width: 16px;
      height: 16px;
      border: 2px solid var(--rule);
      border-radius: 4px;
      cursor: pointer;
      appearance: none;
      background: var(--surface);
      transition: all 0.12s;
      flex-shrink: 0;
      position: relative;
    }
    .kpi-checkbox:checked { background: var(--accent); border-color: var(--accent); }
    .kpi-checkbox:checked::after {
      content: '';
      position: absolute;
      left: 3px; top: 1px;
      width: 5px; height: 8px;
      border: 2px solid #fff;
      border-top: none; border-left: none;
      transform: rotate(45deg);
    }

    /* ── Modal overlay ── */
    .kpi-modal-overlay {
      position: fixed; inset: 0;
      background: rgba(0,0,0,0.45);
      backdrop-filter: blur(2px);
      z-index: 1000;
      display: flex; align-items: center; justify-content: center;
      padding: 20px;
      animation: kpiFadeIn 0.15s ease;
    }
    .kpi-modal-box {
      background: var(--surface);
      border-radius: 16px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.2);
      width: 100%;
      max-height: 90vh;
      overflow: hidden;
      display: flex; flex-direction: column;
      animation: kpiScaleIn 0.2s ease;
    }

  

    /* ── Animations ── */
    @keyframes kpiFadeIn    { from { opacity: 0; }                              to { opacity: 1; } }
    @keyframes kpiScaleIn   { from { opacity: 0; transform: scale(0.96); }     to { opacity: 1; transform: scale(1); } }
    @keyframes kpiSlideDown { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes kpiSpin      { to { transform: rotate(360deg); } }
    @keyframes kpiShimmer   {
      0%   { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    .kpi-slide-down { animation: kpiSlideDown 0.22s ease; }

    /* ── Inline select for filters ── */
    .kpi-inline-select {
      padding: 7px 11px;
      border: 1.5px solid var(--rule);
      border-radius: 8px;
      font-size: 12.5px;
      font-family: var(--font-sans);
      font-weight: 600;
      color: var(--ink-2);
      background: var(--surface);
      cursor: pointer;
      outline: none;
      transition: all 0.15s;
    }
    .kpi-inline-select:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
    .kpi-inline-select.active { border-color: var(--accent); background: var(--accent-3); color: var(--accent); }

    /* ── Upload zone ── */
    .kpi-upload-zone {
      border: 2px dashed var(--rule);
      border-radius: 12px;
      padding: 32px;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s;
      background: var(--surface);
    }
    .kpi-upload-zone:hover, .kpi-upload-zone.dragover {
      border-color: var(--accent);
      background: var(--accent-3);
    }

    /* ── Chat ── */
    .kpi-chat-scrollbar::-webkit-scrollbar { width: 4px; }
    .kpi-chat-scrollbar::-webkit-scrollbar-track { background: transparent; }
    .kpi-chat-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 2px; }

    .kpi-mention-dropdown {
      position: absolute;
      bottom: calc(100% + 8px);
      left: 0; right: 0;
      background: var(--surface);
      border: 1.5px solid var(--rule);
      border-radius: 10px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.12);
      z-index: 100;
      overflow: hidden;
      max-height: 200px;
      overflow-y: auto;
    }
    .kpi-mention-item { padding: 8px 14px; cursor: pointer; font-size: 13px; transition: background 0.1s; }
    .kpi-mention-item:hover { background: var(--surface-2); }

    .mention-highlight {
      background: #dbeafe; color: #1d4ed8;
      border-radius: 3px; padding: 0 3px; font-weight: 600;
    }

    /* ── Spinner ── */
    .kpi-spinner {
      width: 24px; height: 24px;
      border: 2.5px solid var(--rule);
      border-top-color: var(--accent);
      border-radius: 50%;
      animation: kpiSpin 0.7s linear infinite;
      flex-shrink: 0;
    }

    /* ── Dropdown menu ── */
    .kpi-dropdown-menu {
      position: absolute;
      top: calc(100% + 4px); right: 0;
      background: var(--surface);
      border: 1.5px solid var(--rule);
      border-radius: 10px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.12);
      z-index: 200; min-width: 200px;
      overflow: hidden;
      animation: kpiSlideDown 0.15s ease;
    }
    .kpi-dropdown-item {
      display: flex; align-items: center; gap: 10px;
      padding: 10px 14px; font-size: 13px; cursor: pointer;
      transition: background 0.1s; color: var(--ink-2); font-weight: 500;
      font-family: var(--font-sans);
    }
    .kpi-dropdown-item:hover { background: var(--surface-2); }
    .kpi-dropdown-item.danger { color: var(--red); }
    .kpi-dropdown-item.danger:hover { background: var(--red-2); }

    /* ── Bulk action bar ── */
    .kpi-bulk-bar { animation: kpiSlideDown 0.2s ease; }

    /* ── Filter pill buttons ── */
    .kpi-filter-pill {
      padding: 6px 14px;
      border-radius: 8px;
      cursor: pointer;
      font-size: 12px;
      font-weight: 600;
      font-family: var(--font-sans);
      transition: all 0.15s;
      border: 1.5px solid var(--rule);
      background: var(--surface);
      color: var(--ink-2);
    }
    .kpi-filter-pill.active {
      border-color: var(--accent);
      background: var(--accent-3);
      color: var(--accent);
    }

    /* ── Location badge ── */
    .kpi-location-badge {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 4px 12px;
      background: var(--surface);
      border: 1.5px solid var(--rule);
      border-radius: 20px;
      font-size: 12px; font-weight: 600;
      color: var(--ink-2);
    }

    /* ── Search highlight ── */
    .kpi-search-mark {
      background: #fef08a;
      border-radius: 2px;
      padding: 0 1px;
    }

    /* ── Shimmer skeleton ── */
    .kpi-shimmer {
      background: linear-gradient(90deg, #f1f5f9 25%, #e8edf5 50%, #f1f5f9 75%);
      background-size: 200% 100%;
      animation: kpiShimmer 1.4s infinite;
    }

    /* ── Spinner inline for stat cards ── */
    .kpi-card-spinner {
      width: 16px; height: 16px;
      border: 2px solid rgba(0,0,0,0.1);
      border-radius: 50%;
      animation: kpiSpin 0.7s linear infinite;
    }

    .kpi-input-warn {
  border-color: var(--amber) !important;
  background: var(--amber-2) !important;
}
  
  `;
  document.head.appendChild(s);
};